
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const PrivacyPage = () => {
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', title: 'Overview', icon: 'ri-eye-line' },
    { id: 'collection', title: 'Data Collection', icon: 'ri-database-line' },
    { id: 'usage', title: 'Data Usage', icon: 'ri-settings-line' },
    { id: 'sharing', title: 'Data Sharing', icon: 'ri-share-line' },
    { id: 'security', title: 'Security', icon: 'ri-shield-check-line' },
    { id: 'rights', title: 'Your Rights', icon: 'ri-user-settings-line' },
    { id: 'cookies', title: 'Cookies', icon: 'ri-file-text-line' },
    { id: 'contact', title: 'Contact Us', icon: 'ri-mail-line' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Privacy Policy</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
            </p>
            <div className="mt-8 text-blue-100">
              <p>Last updated: December 2024</p>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="bg-gray-50 py-8 sticky top-0 z-10 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto space-x-4 pb-2">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                  activeSection === section.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                <i className={`${section.icon} text-lg`}></i>
                <span>{section.title}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Overview */}
          {activeSection === 'overview' && (
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Privacy Policy Overview</h2>
              <p className="text-gray-600 mb-6">
                At AI Consulting Authority, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered real estate services.
              </p>
              
              <div className="bg-blue-50 p-6 rounded-lg mb-8">
                <h3 className="text-xl font-semibold text-blue-900 mb-4">Key Points</h3>
                <ul className="space-y-2 text-blue-800">
                  <li>• We only collect information necessary to provide our services</li>
                  <li>• Your data is encrypted and stored securely</li>
                  <li>• We never sell your personal information to third parties</li>
                  <li>• You have full control over your data and can request deletion at any time</li>
                  <li>• We comply with GDPR, CCPA, and other privacy regulations</li>
                </ul>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white p-6 rounded-lg border">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">What We Collect</h4>
                  <ul className="text-gray-600 space-y-1">
                    <li>• Contact information</li>
                    <li>• Usage data and analytics</li>
                    <li>• Property and client data</li>
                    <li>• Communication records</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg border">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">How We Use It</h4>
                  <ul className="text-gray-600 space-y-1">
                    <li>• Provide AI services</li>
                    <li>• Improve our platform</li>
                    <li>• Customer support</li>
                    <li>• Legal compliance</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Data Collection */}
          {activeSection === 'collection' && (
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Information We Collect</h2>
              
              <div className="space-y-8">
                <div className="bg-white p-6 rounded-lg border">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Personal Information</h3>
                  <p className="text-gray-600 mb-4">We collect personal information that you provide directly to us:</p>
                  <ul className="text-gray-600 space-y-2">
                    <li>• Name, email address, and phone number</li>
                    <li>• Real estate license information</li>
                    <li>• Company and brokerage details</li>
                    <li>• Billing and payment information</li>
                    <li>• Profile photos and professional information</li>
                  </ul>
                </div>

                <div className="bg-white p-6 rounded-lg border">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Usage Data</h3>
                  <p className="text-gray-600 mb-4">We automatically collect information about how you use our services:</p>
                  <ul className="text-gray-600 space-y-2">
                    <li>• Log data (IP address, browser type, pages visited)</li>
                    <li>• Device information (device type, operating system)</li>
                    <li>• Usage patterns and feature interactions</li>
                    <li>• Performance metrics and error reports</li>
                  </ul>
                </div>

                <div className="bg-white p-6 rounded-lg border">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Property and Client Data</h3>
                  <p className="text-gray-600 mb-4">To provide our AI services, we may process:</p>
                  <ul className="text-gray-600 space-y-2">
                    <li>• Property listings and descriptions</li>
                    <li>• Client contact information and preferences</li>
                    <li>• Market data and analytics</li>
                    <li>• Communication logs and interactions</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Data Usage */}
          {activeSection === 'usage' && (
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">How We Use Your Information</h2>
              
              <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-blue-900 mb-4">Service Provision</h3>
                  <ul className="text-blue-800 space-y-2">
                    <li>• Deliver AI-powered real estate tools and features</li>
                    <li>• Generate leads and automate marketing campaigns</li>
                    <li>• Provide market analytics and insights</li>
                    <li>• Enable client management and communication</li>
                  </ul>
                </div>

                <div className="bg-green-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-green-900 mb-4">Platform Improvement</h3>
                  <ul className="text-green-800 space-y-2">
                    <li>• Analyze usage patterns to enhance features</li>
                    <li>• Train and improve AI algorithms</li>
                    <li>• Develop new tools and capabilities</li>
                    <li>• Optimize platform performance</li>
                  </ul>
                </div>

                <div className="bg-purple-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-purple-900 mb-4">Communication</h3>
                  <ul className="text-purple-800 space-y-2">
                    <li>• Send service updates and notifications</li>
                    <li>• Provide customer support</li>
                    <li>• Share educational content and best practices</li>
                    <li>• Process billing and account management</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Data Sharing */}
          {activeSection === 'sharing' && (
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Information Sharing and Disclosure</h2>
              
              <div className="bg-red-50 p-6 rounded-lg mb-8">
                <h3 className="text-xl font-semibold text-red-900 mb-4">We Never Sell Your Data</h3>
                <p className="text-red-800">
                  We do not sell, rent, or trade your personal information to third parties for marketing purposes.
                </p>
              </div>

              <div className="space-y-6">
                <div className="bg-white p-6 rounded-lg border">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Providers</h3>
                  <p className="text-gray-600 mb-4">We may share information with trusted service providers who help us operate our platform:</p>
                  <ul className="text-gray-600 space-y-2">
                    <li>• Cloud hosting and data storage providers</li>
                    <li>• Payment processing companies</li>
                    <li>• Analytics and monitoring services</li>
                    <li>• Customer support platforms</li>
                  </ul>
                </div>

                <div className="bg-white p-6 rounded-lg border">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Legal Requirements</h3>
                  <p className="text-gray-600 mb-4">We may disclose information when required by law or to:</p>
                  <ul className="text-gray-600 space-y-2">
                    <li>• Comply with legal processes or government requests</li>
                    <li>• Protect our rights and property</li>
                    <li>• Ensure user safety and platform security</li>
                    <li>• Investigate fraud or abuse</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Security */}
          {activeSection === 'security' && (
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Data Security</h2>
              
              <div className="bg-green-50 p-6 rounded-lg mb-8">
                <h3 className="text-xl font-semibold text-green-900 mb-4">Enterprise-Grade Security</h3>
                <p className="text-green-800">
                  We implement industry-leading security measures to protect your data from unauthorized access, disclosure, or destruction.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg border">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Technical Safeguards</h4>
                  <ul className="text-gray-600 space-y-2">
                    <li>• AES-256 encryption at rest</li>
                    <li>• TLS 1.3 encryption in transit</li>
                    <li>• Multi-factor authentication</li>
                    <li>• Regular security audits</li>
                    <li>• Intrusion detection systems</li>
                  </ul>
                </div>

                <div className="bg-white p-6 rounded-lg border">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Operational Security</h4>
                  <ul className="text-gray-600 space-y-2">
                    <li>• Access controls and permissions</li>
                    <li>• Employee security training</li>
                    <li>• Incident response procedures</li>
                    <li>• Regular backups and recovery</li>
                    <li>• Compliance monitoring</li>
                  </ul>
                </div>
              </div>

              <div className="bg-blue-50 p-6 rounded-lg mt-8">
                <h4 className="text-lg font-semibold text-blue-900 mb-3">Certifications & Compliance</h4>
                <div className="grid md:grid-cols-3 gap-4 text-blue-800">
                  <div>• SOC 2 Type II</div>
                  <div>• GDPR Compliant</div>
                  <div>• CCPA Compliant</div>
                  <div>• ISO 27001</div>
                  <div>• HIPAA Ready</div>
                  <div>• PCI DSS</div>
                </div>
              </div>
            </div>
          )}

          {/* Your Rights */}
          {activeSection === 'rights' && (
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Your Privacy Rights</h2>
              
              <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-blue-900 mb-4">Access and Control</h3>
                  <p className="text-blue-800 mb-4">You have the right to:</p>
                  <ul className="text-blue-800 space-y-2">
                    <li>• Access your personal information</li>
                    <li>• Update or correct your data</li>
                    <li>• Delete your account and data</li>
                    <li>• Export your data</li>
                    <li>• Restrict processing</li>
                  </ul>
                </div>

                <div className="bg-white p-6 rounded-lg border">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">How to Exercise Your Rights</h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <i className="ri-settings-line text-blue-600 text-xl mt-1"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900">Account Settings</h4>
                        <p className="text-gray-600">Access and update your information through your account dashboard.</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-mail-line text-blue-600 text-xl mt-1"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900">Email Request</h4>
                        <p className="text-gray-600">Contact privacy@aiconsultingauthority.com for data requests.</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-customer-service-line text-blue-600 text-xl mt-1"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900">Support Team</h4>
                        <p className="text-gray-600">Chat with our support team for immediate assistance.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-yellow-900 mb-4">Response Time</h3>
                  <p className="text-yellow-800">
                    We will respond to your privacy requests within 30 days. For urgent matters, we aim to respond within 72 hours.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Cookies */}
          {activeSection === 'cookies' && (
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Cookie Policy</h2>
              
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-lg border">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">What Are Cookies?</h3>
                  <p className="text-gray-600">
                    Cookies are small text files stored on your device that help us provide a better user experience and analyze how our platform is used.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-green-50 p-6 rounded-lg">
                    <h4 className="text-lg font-semibold text-green-900 mb-3">Essential Cookies</h4>
                    <p className="text-green-800 mb-3">Required for basic functionality:</p>
                    <ul className="text-green-800 space-y-1">
                      <li>• Authentication and security</li>
                      <li>• Session management</li>
                      <li>• Load balancing</li>
                      <li>• Error tracking</li>
                    </ul>
                  </div>

                  <div className="bg-blue-50 p-6 rounded-lg">
                    <h4 className="text-lg font-semibold text-blue-900 mb-3">Analytics Cookies</h4>
                    <p className="text-blue-800 mb-3">Help us improve our services:</p>
                    <ul className="text-blue-800 space-y-1">
                      <li>• Usage statistics</li>
                      <li>• Performance monitoring</li>
                      <li>• Feature adoption</li>
                      <li>• User behavior analysis</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-purple-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-purple-900 mb-4">Cookie Management</h3>
                  <p className="text-purple-800 mb-4">You can control cookies through:</p>
                  <ul className="text-purple-800 space-y-2">
                    <li>• Browser settings and preferences</li>
                    <li>• Our cookie consent banner</li>
                    <li>• Account privacy settings</li>
                    <li>• Third-party opt-out tools</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Contact */}
          {activeSection === 'contact' && (
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Our Privacy Team</h2>
              
              <div className="bg-blue-50 p-6 rounded-lg mb-8">
                <h3 className="text-xl font-semibold text-blue-900 mb-4">Questions About This Policy?</h3>
                <p className="text-blue-800">
                  Our privacy team is here to help. Contact us with any questions about how we handle your personal information.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <i className="ri-mail-line text-blue-600 text-2xl mt-1"></i>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">Email</h4>
                      <p className="text-gray-600">privacy@aiconsultingauthority.com</p>
                      <p className="text-sm text-gray-500">Response within 24 hours</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <i className="ri-phone-line text-blue-600 text-2xl mt-1"></i>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">Phone</h4>
                      <p className="text-gray-600">1-800-AI-PRIVACY</p>
                      <p className="text-sm text-gray-500">Mon-Fri, 9 AM - 6 PM EST</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <i className="ri-map-pin-line text-blue-600 text-2xl mt-1"></i>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">Address</h4>
                      <p className="text-gray-600">
                        AI Consulting Authority<br />
                        Privacy Department<br />
                        123 Innovation Drive<br />
                        San Francisco, CA 94105
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg border">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Data Protection Officer</h4>
                  <p className="text-gray-600 mb-4">
                    For GDPR-related inquiries, contact our Data Protection Officer:
                  </p>
                  <div className="space-y-2 text-gray-600">
                    <p>Sarah Johnson, CIPP/E</p>
                    <p>dpo@aiconsultingauthority.com</p>
                    <p>+1 (555) 123-4567</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg mt-8">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Policy Updates</h4>
                <p className="text-gray-600">
                  We may update this Privacy Policy from time to time. We will notify you of any material changes by email or through our platform. The "Last updated" date at the top of this policy indicates when it was last revised.
                </p>
              </div>
            </div>
          )}

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
